import { useState } from 'react';
import type React from 'react';
import { AlertTriangle, Globe, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { usePublish, type ValidationError } from '@/api/publishing';

interface PublishButtonProps {
  syncedLocationId: string;
  isEnriched: boolean;
  disabled?: boolean;
  accountDisabled?: boolean;
  isBlocked?: boolean;
  onSuccess?: () => void;
}

export function PublishButton({
  syncedLocationId,
  isEnriched,
  disabled = false,
  accountDisabled = false,
  isBlocked = false,
  onSuccess,
}: PublishButtonProps): JSX.Element {
  const { toast } = useToast();
  const publish = usePublish();
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [overridableErrors, setOverridableErrors] = useState<ValidationError[]>([]);

  const handlePublish = async () => {
    try {
      const result = await publish.mutateAsync({ syncedLocationId });

      if (!result.success) {
        // Check if this can be force-published
        if ('isForcePublishable' in result && result.isForcePublishable &&
            'validationErrors' in result && result.validationErrors) {
          setOverridableErrors(result.validationErrors.filter((e) => e.overridable));
          setForceDialogOpen(true);
          return;
        }

        // Build error description with validation details if available
        let errorDescription: React.ReactNode = result.error;

        if ('validationErrors' in result && result.validationErrors && result.validationErrors.length > 0) {
          errorDescription = (
            <div className="space-y-2">
              <p>{result.error}</p>
              <ul className="list-disc pl-4 space-y-1">
                {result.validationErrors.map((err, index) => (
                  <li key={index}>{err.message}</li>
                ))}
              </ul>
            </div>
          );
        } else if (
          'availableCurrencies' in result &&
          result.availableCurrencies &&
          // The backend's refusal already names the field and the allowed codes;
          // append them only for an older message that does not.
          !result.availableCurrencies.every((code) => result.error.includes(code))
        ) {
          errorDescription = `${result.error} Available currencies: ${result.availableCurrencies.join(', ')}`;
        }

        toast({
          title: 'Failed to Publish',
          description: errorDescription,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Location Published',
        description: 'The location is now live on ScoutAround.',
      });

      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish location';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleForcePublish = async () => {
    setForceDialogOpen(false);
    try {
      const result = await publish.mutateAsync({ syncedLocationId, force: true });

      if (!result.success) {
        toast({
          title: 'Failed to Force Publish',
          description: result.error,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Location Published',
        description: 'The location was force-published to ScoutAround (some fields were skipped).',
      });

      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish location';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const isDisabled = disabled || !isEnriched || accountDisabled || isBlocked || publish.isPending;

  return (
    <>
      <Button
        onClick={handlePublish}
        disabled={isDisabled}
        data-testid="publish-btn"
      >
        {publish.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Globe className="mr-2 h-4 w-4" />
        )}
        Publish to ScoutAround
      </Button>

      <AlertDialog open={forceDialogOpen} onOpenChange={setForceDialogOpen}>
        <AlertDialogContent data-testid="force-publish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Force Publish?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This location is missing some recommended fields. You can force-publish
                  it, but the listing may be incomplete on ScoutAround.
                </p>
                <ul className="list-disc pl-4 space-y-1 text-sm text-amber-600">
                  {overridableErrors.map((err, index) => (
                    <li key={index}>{err.message}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="force-publish-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForcePublish}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="force-publish-confirm-btn"
            >
              Force Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
